import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField, Button, Grid2, Checkbox, Snackbar, Alert } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../services/authentication/authContext';
import { useTranslation } from 'react-i18next';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import { taskServices } from '../../services/taskServices';
import '../../css/Form.css';
import PageHeader from '../ui/PageHeader';
import ConfirmationDialog from '../common/ConfirmationDialog';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ExtraTaskForm = () => {
  const [taskTitle, setTaskTitle] = useState([]);
  const [taskDescription, setTaskDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [taskId, setTaskId] = useState('');
  const task = location.state?.taskData
  const id = taskId;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);
  const [taskTitleData] = useState([
    { "value": 1, "label": "Office Meeting" },
    { "value": 2, "label": "Free" },
    { "value": 3, "label": "Attending Training" },
    { "value": 4, "label": "Providing Training" },
    { "value": 5, "label": "Break" },
    { "value": 6, "label": "Fun Activity/Celebration" },
    { "value": 7, "label": "Email/Workflow" },
    { "value": 8, "label": "Filling of VAT/A/c/Tax" },
    { "value": 9, "label": "Guiding to Team Member" },
    { "value": 10, "label": "10 Minutes Reading" },
    { "value": 11, "label": "QC Review"},
    { "value": 12, "label": "Marketing"},
    { "value": 13, "label": "Task Assignment"},
    { "value": 14, "label": "IT"},
  ]);
  const navigationUrl = "/home/extra-tasks";

  useEffect(() => {
    if (task) {
      setTaskId(task.id);
    }
  }, [task]);

  useEffect(() => {
    if (accessToken) {
      if (taskId) {
        setLoading(true);
        taskServices.getTaskDataById(taskId)
          .then((response) => {
            setTaskTitle(response.titles);
            setTaskDescription(response.description);
            setLoading(false);

          })
          .catch((error) => {
            setError(error.message);
            setTimeout(() => {
              setError(null);
            }, 5000);
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    }
  }, [taskId, accessToken]);

  const handleSubmit = (event) => {
    event.preventDefault();

    setIsSubmitting(true);
    const taskTitles = Array.isArray(taskTitle) ? taskTitle.map(item => item.label) : [taskTitle];

    const formData = {
      titles: taskTitles,
      description: taskDescription,
    };

    const formUpdate = {
      id,
      titles: taskTitles,
      description: taskDescription,
    };

    if (accessToken) {
      if (taskId) {
        taskServices.updateTaskData(formUpdate)
          .then((updateTask) => {
            localStorage.setItem("successMessage", "Task Updated successfully!");
            navigate(navigationUrl);
          })
          .catch((error) => {
            if (error.response?.status === 400) {
              setSnackbarMessage(error.response.data);
            } else {
              setSnackbarMessage(error.message);
            }
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          });

      } else {
        taskServices.postTaskData(formData)
          .then((newExtraTask) => {
            localStorage.setItem("successMessage", "Task added successfully!");
            navigate(navigationUrl);
          })
          .catch((error) => {
            if (error.response?.status === 400) {
              setSnackbarMessage(error.response.data);
            } else {
              setSnackbarMessage(error.message);
            }
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          });
      }
    }
  };

  const handleCancel = () => {
    handleBack();
  };

  const handleBack = () => {
    if (isDirty) {
      setShowBackConfirmation(true);
    } else {
      navigate(navigationUrl);
    }
  };

  const handleConfirmBack = () => {
    setShowBackConfirmation(false);
    navigate(navigationUrl);
  };

  const handleTaskTitleChange = (event, value, reason) => {
    setTaskTitle(value || []);
    if (!loading && (reason === 'input' || reason === 'selectOption' || reason === 'removeOption' || reason === 'clear')) {
      setIsDirty(true);
    }
  };

  return (
    <div className="main-form-container">
      <PageHeader 
        title={taskId ? "Edit Extra Task" : "Add Extra Task"} 
        left={
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
        }
      />
      <form onSubmit={handleSubmit} className="form" onChange={() => { if (!loading) setIsDirty(true); }}>
        <Grid2 container spacing={3}>

          <Grid2 size={{ xs: 12, md: 6 }}>
            {taskId ? (
              <Autocomplete
                value={taskTitle}
                onInputChange={handleTaskTitleChange}
                options={taskTitleData.map((option) => option.label)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('task.taskTitle')}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    required
                  />
                )}
                disableClearable
                freeSolo
                loading={loading}
                noOptionsText={t('noOptions')}
                disabled
              />
            ) : (
              <Autocomplete
                multiple
                value={taskTitle}
                onChange={handleTaskTitleChange}
                options={taskTitleData}
                getOptionLabel={(option) => option.label}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => {
                  const { key, ...restProps } = props;

                  return (
                    <li key={key} {...restProps}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option.label}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('task.taskTitle')} variant="outlined" fullWidth margin="normal" />
                )}
                loading={loading}
                noOptionsText={t('noOptions')}
              />)}
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            {taskId ? (
              <TextField
                label={t('task.desc')}
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={4}
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                placeholder="Enter Task Description"
                required
              />
            ) : (
              <TextField
                label={t('task.desc')}
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={4}
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                placeholder="Enter Task Description"
              />
            )}
          </Grid2>

          <Grid2 size={{ xs: 12 }} className="form-actions" sx={{ flexDirection: { xs: 'row !important', sm: 'row' }, gap: { xs: 2, sm: 0 }, '& > button': { flex: { xs: 1, sm: 'none' }, margin: { xs: '0 !important', sm: '' } } }}>
            {taskId ?
              (<Button type="submit" variant="contained" className="form-button"
                startIcon={<EditIcon />}>
                {t('task.update')}
              </Button>
              ) : (
                <Button type="submit" variant="contained" className="form-button"
                  startIcon={<SaveIcon />}>
                  {t('task.save')}
                </Button>
              )}
            <Button type="button" variant="contained" color="inherit" onClick={handleCancel} className="form-button-reset"
              startIcon={<CancelIcon />}>
              {t('task.cancel')}
            </Button>
          </Grid2>
        </Grid2>
      </form>

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ whiteSpace: 'pre-line' }}>
          {Array.isArray(snackbarMessage) ? (
            snackbarMessage.map((msg, index) => (
              <div key={index}>{msg}</div>
            ))
          ) : (
            snackbarMessage
          )}
        </Alert>
      </Snackbar>

      <ConfirmationDialog
        open={showBackConfirmation}
        onClose={() => setShowBackConfirmation(false)}
        onConfirm={handleConfirmBack}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave this page?"
        confirmLabel="Leave Page"
        confirmColor="error"
      />
    </div>
  );
};

export default ExtraTaskForm;
